import React from 'react';
import './credits-page.css';

interface PackageCredit {
  name: string;
  version: string;
  description: string;
  license: string;
  npm?: string;
  github?: string;
  website?: string;
}

const packages: PackageCredit[] = [
  {
    name: 'React',
    version: '18',
    description: 'The library for web and native user interfaces.',
    license: 'MIT',
    npm: 'https://www.npmjs.com/package/react',
    github: 'https://github.com/facebook/react',
    website: 'https://react.dev',
  },
  {
    name: 'Tone.js',
    version: '15',
    description: 'A Web Audio framework for creating interactive music in the browser.',
    license: 'MIT',
    npm: 'https://www.npmjs.com/package/tone',
    github: 'https://github.com/Tonejs/Tone.js',
    website: 'https://tonejs.github.io',
  },
  {
    name: 'Recoil',
    version: '0.7',
    description: 'A state management library for React.',
    license: 'MIT',
    npm: 'https://www.npmjs.com/package/recoil',
    github: 'https://github.com/facebookexperimental/Recoil',
    website: 'https://recoiljs.org',
  },
  {
    name: 'TypeScript',
    version: '4',
    description: 'A strongly typed programming language that builds on JavaScript.',
    license: 'Apache-2.0',
    npm: 'https://www.npmjs.com/package/typescript',
    github: 'https://github.com/microsoft/TypeScript',
    website: 'https://www.typescriptlang.org',
  },
  {
    name: 'Create React App',
    version: '5',
    description: 'Set up a modern web app by running one command (react-scripts).',
    license: 'MIT',
    npm: 'https://www.npmjs.com/package/react-scripts',
    github: 'https://github.com/facebook/create-react-app',
  },
  {
    name: 'Testing Library',
    version: '',
    description: 'Simple and complete testing utilities for React components.',
    license: 'MIT',
    npm: 'https://www.npmjs.com/package/@testing-library/react',
    github: 'https://github.com/testing-library/react-testing-library',
    website: 'https://testing-library.com',
  },
  {
    name: 'web-vitals',
    version: '2',
    description: "Google's library for measuring essential web performance metrics.",
    license: 'Apache-2.0',
    npm: 'https://www.npmjs.com/package/web-vitals',
    github: 'https://github.com/GoogleChrome/web-vitals',
  },
  {
    name: 'Tiny5',
    version: '',
    description: 'A pixel/retro font used for headings and labels.',
    license: 'OFL-1.1',
    website: 'https://fonts.google.com/specimen/Tiny5',
  },
  {
    name: 'Ubuntu Mono',
    version: '',
    description: 'A monospace font used for body text and values.',
    license: 'OFL-1.1',
    website: 'https://fonts.google.com/specimen/Ubuntu+Mono',
  },
];

const faqItems = [
  {
    q: 'Do I need to install anything to use SynthKitty?',
    a: 'No — SynthKitty runs entirely in your web browser. There is nothing to download or install. As long as your browser supports the Web Audio API (all modern browsers do), you are ready to play.',
  },
  {
    q: 'Is SynthKitty free to use?',
    a: 'Yes, SynthKitty is completely free. There are no subscriptions, no accounts, and no paywalls. Simply open the website and start making music.',
  },
  {
    q: 'What is an ADSR envelope?',
    a: 'ADSR stands for Attack, Decay, Sustain, and Release — the four stages that describe how a synthesizer note changes in volume over time. Attack is how quickly the note fades in. Decay is how fast it drops to the Sustain level after the initial peak. Sustain is the steady volume held while the key is pressed. Release is how long the note takes to fade out after you let go.',
  },
  {
    q: 'What waveform types are available?',
    a: 'SynthKitty offers four classic synthesizer waveforms: Sine (smooth and pure), Square (hollow and buzzy), Sawtooth (bright and edgy), and Triangle (mellow, like a softened square). Each has a distinct character that suits different musical styles.',
  },
  {
    q: 'Can I play multiple notes at the same time?',
    a: 'Yes! SynthKitty uses a polyphonic synthesizer, so you can press multiple piano keys simultaneously to play chords. This works with both mouse clicks and touch input on mobile devices.',
  },
  {
    q: 'Does SynthKitty work on mobile and tablet?',
    a: 'Yes. The piano keyboard and all controls are touch-friendly. SynthKitty uses pointer events so it responds correctly to both finger taps and mouse clicks on any device.',
  },
  {
    q: 'What is the Composer tab?',
    a: 'The Composer is a simple step sequencer built into SynthKitty. You can record notes by playing the piano while recording is active, set the tempo (BPM), choose the number of bars, and play your composition back in a loop. Compositions can also be saved and shared.',
  },
  {
    q: 'Can I save and share my compositions?',
    a: 'Yes. The Projects tab lets you name and save any number of compositions to your browser\'s local storage. You can also export all your projects to a JSON file for backup, or share a composition via a link that encodes the project data.',
  },
  {
    q: 'Does SynthKitty support MIDI devices?',
    a: 'SynthKitty includes experimental MIDI input support via the Web MIDI API. If your browser supports MIDI and you connect a MIDI keyboard, SynthKitty will attempt to detect it and map note input automatically.',
  },
  {
    q: 'Why does the cat logo shake?',
    a: 'The SynthKitty mascot shakes and changes expression whenever a piano key is pressed — it is her way of dancing along to the music! This playful animation is part of the Kawaii design philosophy at the heart of SynthKitty.',
  },
];

export const CreditsPage = () => {
  return (
    <div className="sk-credits-page">

      {/* ── About ── */}
      <section className="sk-credits-about">
        <h2 className="pixel-font sk-credits-section-title">About SynthKitty</h2>
        <p>
          SynthKitty is a free, browser-based synthesizer and virtual piano built around the Japanese
          design philosophy of <strong>Kawaii</strong> (かわいい) — cuteness, softness, and joy. It
          combines a fully functional 8-octave polyphonic synthesizer with a charming cat mascot,
          pastel aesthetics, and pixel-art typography to make music creation feel approachable and
          delightful for everyone.
        </p>
        <p>
          Whether you are a curious beginner exploring what a synthesizer does, a music enthusiast
          wanting a quick way to sketch melodies, or simply someone who enjoys the satisfying click of
          a well-designed virtual piano, SynthKitty is built for you. No accounts, no downloads, no
          cost — just open the page and play.
        </p>
        <p>
          Under the hood, SynthKitty is powered by the Web Audio API through the{' '}
          <a href="https://tonejs.github.io" target="_blank" rel="noopener noreferrer">Tone.js</a>{' '}
          framework, which enables real-time audio synthesis entirely inside the browser. The interface
          is built with React and TypeScript, and the whole project is open source.
        </p>
      </section>

      {/* ── How to Use ── */}
      <section className="sk-credits-howto">
        <h2 className="pixel-font sk-credits-section-title">How to use SynthKitty</h2>
        <ol className="sk-credits-steps">
          <li>
            <strong>Play the piano.</strong> Click or tap any key on the on-screen keyboard at the
            bottom of the page to trigger a note. Hold multiple keys at once to play chords.
          </li>
          <li>
            <strong>Choose a waveform.</strong> Open the <em>Oscillators</em> tab and select Sine,
            Square, Sawtooth, or Triangle to change the tone character of the synthesizer.
          </li>
          <li>
            <strong>Shape the sound with ADSR.</strong> In the Oscillators tab, adjust the Attack,
            Decay, Sustain, and Release sliders to control how each note fades in, holds, and fades
            out. Short attack and release values give a punchy, plucked feel; longer values produce
            smooth, pad-like tones.
          </li>
          <li>
            <strong>Adjust volume and add a filter.</strong> Use the Volume knob to set the output
            level. Enable the Low Pass or High Pass filter and sweep the cutoff to brighten or
            darken the sound.
          </li>
          <li>
            <strong>Record a composition.</strong> Switch to the <em>Composer</em> tab, press the
            record button, then play notes on the piano. SynthKitty captures the timing of every
            note. When done, stop recording and press Play to hear your piece back. Toggle looping
            to repeat it continuously.
          </li>
          <li>
            <strong>Save and share.</strong> Head to the <em>Projects</em> tab, give your composition
            a name, and click Save. Use the Share button to generate a link you can send to friends,
            or Export to download all your projects as a JSON backup file.
          </li>
        </ol>
      </section>

      {/* ── Features ── */}
      <section className="sk-credits-features">
        <h2 className="pixel-font sk-credits-section-title">Features</h2>
        <ul className="sk-credits-feature-list">
          <li>8-octave polyphonic virtual piano keyboard (C0 – B7, 96 keys)</li>
          <li>Four classic synthesizer waveforms: Sine, Square, Sawtooth, Triangle</li>
          <li>Full ADSR envelope control (Attack, Decay, Sustain, Release)</li>
          <li>Volume control (−30 dB to 0 dB)</li>
          <li>Low Pass and High Pass filters with adjustable cutoff</li>
          <li>Transpose control to shift pitch up or down by semitones</li>
          <li>Step composer / sequencer with BPM control and loop mode</li>
          <li>Built-in demo compositions (Twinkle Twinkle, Für Elise, Ode to Joy)</li>
          <li>Save unlimited named projects to local storage</li>
          <li>Export and import projects as JSON files</li>
          <li>Shareable composition links</li>
          <li>Experimental Web MIDI input support</li>
          <li>Fully responsive — works on desktop, tablet, and mobile</li>
          <li>Runs 100% offline in the browser — no server or account required</li>
        </ul>
      </section>

      {/* ── FAQ ── */}
      <section className="sk-credits-faq">
        <h2 className="pixel-font sk-credits-section-title">Frequently asked questions</h2>
        <dl className="sk-credits-faq-list">
          {faqItems.map(({ q, a }) => (
            <React.Fragment key={q}>
              <dt className="sk-credits-faq-q pixel-font">{q}</dt>
              <dd className="sk-credits-faq-a">{a}</dd>
            </React.Fragment>
          ))}
        </dl>
      </section>

      {/* ── Creator ── */}
      <section className="sk-credits-creator">
        <div className="sk-credits-creator-badge pixel-font">✦ MADE WITH ♥ BY ✦</div>
        <a
          href="https://equites.digital"
          target="_blank"
          rel="noopener noreferrer"
          className="sk-credits-creator-link"
        >
          <span className="pixel-font">Equites Digital</span>
        </a>
        <p className="sk-credits-creator-tagline">Visit us at equites.digital</p>
      </section>

      {/* ── Technologies ── */}
      <section className="sk-credits-packages">
        <h2 className="pixel-font sk-credits-section-title">Technologies used</h2>
        <ul className="sk-credits-list">
          {packages.map((pkg) => (
            <li key={pkg.name} className="sk-credits-item">
              <div className="sk-credits-item-header">
                <span className="sk-credits-item-name pixel-font">{pkg.name}</span>
                {pkg.version && (
                  <span className="sk-credits-item-version">v{pkg.version}</span>
                )}
                <span className="sk-credits-item-license">{pkg.license}</span>
              </div>
              <p className="sk-credits-item-description">{pkg.description}</p>
              <div className="sk-credits-item-links">
                {pkg.website && (
                  <a href={pkg.website} target="_blank" rel="noopener noreferrer">
                    Website
                  </a>
                )}
                {pkg.github && (
                  <a href={pkg.github} target="_blank" rel="noopener noreferrer">
                    GitHub
                  </a>
                )}
                {pkg.npm && (
                  <a href={pkg.npm} target="_blank" rel="noopener noreferrer">
                    npm
                  </a>
                )}
              </div>
            </li>
          ))}
        </ul>
      </section>

    </div>
  );
};
